import { ATTR_NAME } from 'constants/attr-name'
import { CLASS_NAME } from 'constants/class-names'
import { Nullable } from 'constants/types'
import {
    AriaAttr,
    EventType,
    SlidingDrawerEvent,
    LocalStorageKey
} from 'constants/enums'
import { validateEmail } from 'utils/string'
import { AbstractObserver } from 'utils/abstract-classes/abstract-observer'
import { dispatchAnalyticsFormSubmissionEvent } from 'utils/analytics'
import { isDomReady } from 'utils/dom'
import { Sailthru } from './types'
import { logger } from 'utils/system/logger'
import { ScriptService } from 'services/script/script.service'
import { ApiService } from 'services/api/api.service'
import { SailthruUserProfile } from './types'
import { attachEventHandlerWithFemFlagCheck } from 'utils/events'

export enum NewsletterFormType {
    AUTHOR = 'author',
    SINGLE = 'single',
    MULTIPLE = 'multiple'
}

export enum EmailServices {
    SAILTHRU = 'sailthru',
    MAILCHIMP = 'mailchimp'
}

const { newsletter: className } = CLASS_NAME
const {
    widget: { newsletter: attrName }
} = ATTR_NAME

// Not present in typescript yet
interface SubmitEvent extends Event {
    submitter: HTMLElement
}

export class Newsletter extends AbstractObserver {
    public static scriptSource = 'https://ak.sail-horizon.com/spm/spm.v1.min.js'

    public sailthru: Sailthru
    public listIds: Array<string> = []
    public formType: NewsletterFormType
    private accountId: string
    private emailServiceName: string
    private emailValue: string
    private drawerId: string
    private authorName: string
    private originalErrorInnerHTML: string
    private displayedErrorInnerHTML: string

    private form: Nullable<HTMLFormElement>
    private formErrorMsg: Nullable<HTMLElement>
    private formSuccessMsg: Nullable<HTMLElement>
    private successReplaceAreas: Nullable<HTMLElement[]>
    private listCheckboxes: Nullable<HTMLInputElement[]>
    private email: Nullable<HTMLInputElement>
    private emailField: Nullable<HTMLElement>
    private emailError: Nullable<HTMLElement>
    private captchaError: Nullable<HTMLElement>
    private totalDisplay: Nullable<HTMLElement>
    private authorNameInput: Nullable<HTMLInputElement>

    // eslint-disable-next-line no-magic-numbers
    private readonly drawerTimeout = 5000
    private closeTimeout: ReturnType<typeof setTimeout> | undefined
    private hasSlidingDrawer: boolean
    private submitSuccess: boolean
    private drawerEl: Nullable<HTMLElement>
    private isSubscribed: boolean
    private unsubscribeBtn: HTMLButtonElement

    public constructor(protected el: HTMLElement) {
        super(el)
        // Get which form type:
        // multiple  url: https://nationalpost.com/newsletters/
        // single    url: https://nationalpost.com/ Newsletter widget (category, story)
        // author    url: https://nationalpost.com/author/adam-zivo/newsletters/
        this.formType = this.el.getAttribute(
            attrName.type
        ) as NewsletterFormType
        this.activateObserver()
    }

    /**
     * Load the data and show the widget (if data was returned)
     */
    public render(): void {
        // Get Sailthru Bearer authorization from HTML template to authorize Sailthru calls
        const accountId = this.el.getAttribute(attrName.accountId)

        logger('info', 'rendering newsletter')
        if (!accountId) {
            return
        }
        this.accountId = accountId
        this.emailServiceName =
            accountId === 'mc_account'
                ? EmailServices.MAILCHIMP
                : EmailServices.SAILTHRU

        if (this.accountId) {
            this.loadSourceScript().catch((err) => logger('error', err))
        } else {
            logger(
                'error',
                'Cannot initialize newsletter component, data-account-id required'
            )
        }

        // Get smart list for author newsletter
        const authorName = this.el.getAttribute(attrName.authorName) || ''
        this.authorName = authorName

        // * Set Elements
        // is the form the component or is the form inside the component:
        this.form = this.el.classList.contains(className.form)
            ? (this.el as HTMLFormElement)
            : this.getElement<HTMLFormElement>(`.${className.form}`, this.el)
        this.formErrorMsg = this.getElement(
            `.${className.formSubmitError}`,
            this.el
        )
        this.originalErrorInnerHTML = this.formErrorMsg?.innerHTML || ''
        this.displayedErrorInnerHTML = this.originalErrorInnerHTML
        this.formSuccessMsg = this.getElement(
            `.${className.formSubmitSuccess}`,
            this.el
        )
        this.successReplaceAreas = this.getElements(
            `.${className.replaceEl}`,
            this.el
        )
        this.totalDisplay = this.getElement(`[${attrName.total}]`, this.el)

        if (this.form) {
            this.listCheckboxes = this.getElements<HTMLInputElement>(
                `[${attrName.listId}]`,
                this.form
            )
            this.email = this.getElement<HTMLInputElement>(
                `.${className.email}`,
                this.form
            )
            this.emailField = this.getElement(
                `.${className.emailField}`,
                this.form
            )
            this.emailError = this.getElement(
                `.${className.emailError}`,
                this.form
            )
            this.captchaError = this.getElement(
                `.${className.captchaError}`,
                this.form
            )
            this.authorNameInput = this.getElement(
                `.${className.authorNameInput}`,
                this.form
            )
        }

        if (this.formType === NewsletterFormType.AUTHOR) {
            const unsubscribeBtn = this.getElement<HTMLButtonElement>(
                `.${className.cardUnsubscribeButton}`,
                this.form
            )
            if (!unsubscribeBtn) {
                return
            }
            this.unsubscribeBtn = unsubscribeBtn
            this.handleAuthorNewsletterPage()
        }

        if (
            this.formType === NewsletterFormType.SINGLE ||
            this.formType === NewsletterFormType.AUTHOR
        ) {
            // Get Sailthru target list name from HTML template
            const listId = this.el.getAttribute(attrName.listId) || ''
            this.listIds.push(listId)
        }

        if (
            this.formType === NewsletterFormType.MULTIPLE ||
            this.formType === NewsletterFormType.AUTHOR
        ) {
            if (this.form) {
                const drawer = this.getElement(
                    `[${ATTR_NAME.main.slidingDrawer._}]`,
                    this.form
                )
                this.drawerEl = drawer
                if (this.drawerEl) {
                    this.drawerId = this.drawerEl.id
                }
                this.hasSlidingDrawer = drawer ? true : false
            }
            // For MULTIPLE Newsletters
            this.attachChangeListener()
        }
        this.getLRToken(
            () => {
                const exclusiveButton = document.getElementById(
                    attrName.exclusiveCta
                )
                if (exclusiveButton) {
                    const url = exclusiveButton.dataset.url
                    exclusiveButton.outerHTML =
                        `<a class="button button--primary exclusive-cta" href="${url}"` +
                        ' name="newsletters-exclusive-cta">Subscribe</a>'
                }
            },
            () => {
                // token is available in local storage
                const loader = document.getElementById(
                    'js-pn-id-ready-page-loader'
                )
                if (loader) {
                    loader.setAttribute('hx-trigger', 'load')
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    ;(window.htmx as any)?.process(loader)
                }
            }
        )
        this.attachSubmitListener()
        this.attachPaidNewsLetterListener()
    }

    private handleAuthorNewsletterPage(): void {
        const url: URL = new URL(window.location.href)

        // Get LRTokenkey to define logged in user
        const lrtKey = localStorage.getItem(LocalStorageKey.LRTOKENKEY) || ''

        // Redirect to sign-in page if user is not logged in
        if (!lrtKey) {
            // Set redirect url to author newsletter page after sign-in/register
            sessionStorage.setItem('redirect_url', url.toString())

            url.pathname = '/sign-in/'
            window.location.assign(url.toString())
        }

        // Define the user is logged in with LRTokenKey
        // Get the user email from x-id value in local storage if user logged in
        // Auto populate email in input value and display as string
        const xId = JSON.parse(
            localStorage.getItem(LocalStorageKey.XID) || '{}'
        )
        if (xId.data && xId.data.email && this.email) {
            const emailText = this.getElement(
                `.${className.emailText}`,
                this.form
            )
            if (emailText) {
                emailText.innerHTML = xId.data.email
            }
            this.email.value = xId.data.email

            new ApiService()
                .fetchSailthruClient(lrtKey)
                .then(async (res) => {
                    this.handleSailthruResponse(res)
                })
                .catch((err) => {
                    logger('error', 'fetchSailthruClient: ' + err)
                })
        } else {
            logger('error', 'Get user email from localStorage failed')
        }
    }

    private handleSailthruResponse(res: SailthruUserProfile): void {
        // Replace space between first and last name to underscore to match vars key from user profile
        const author_name = this.authorName.split(' ').join('_')

        // check the value from vars key to define the user is subscribed for author
        if (
            res.vars &&
            Object.keys(res.vars).includes(author_name) &&
            res.vars[author_name] === 'Y'
        ) {
            // if 'Y', the newsletter is already subscribed
            this.isSubscribed = true
            this.unsubscribeBtn.hidden = false

            // hide drawer from both keyboard and screen reader
            if (this.drawerEl) {
                this.drawerEl.hidden = true
            }
        } else {
            // if 'N' or author name is not exist, the newsletter is not subscribed
            this.isSubscribed = false
            this.unsubscribeBtn.hidden = true

            // open sign up drawer
            window.dispatchEvent(
                new CustomEvent(SlidingDrawerEvent.OPEN, {
                    detail: this.drawerId
                })
            )
        }
        return
    }

    // Attach a submit handler
    private attachSubmitListener(): void {
        if (this.form) {
            this.form.addEventListener(
                EventType.SUBMIT,
                (event: SubmitEvent) => {
                    event.preventDefault()
                    let action = 'subscribe'
                    if (event.submitter) {
                        // event.submitter can be not present on older browsers
                        // https://caniuse.com/mdn-api_submitevent_submitter
                        action =
                            event.submitter.dataset['action'] || 'subscribe'
                    }
                    if (this.validateEmail()) {
                        this.submitForm().catch((e) => logger.error(e))
                        if (this.authorNameInput)
                            dispatchAnalyticsFormSubmissionEvent(
                                `Author Newsletter - ${action}`,
                                this.authorNameInput,
                                true
                            )
                    }
                }
            )
        }
    }

    /*
     * Check recaptcha
     */
    async getRecaptchaResponse(): Promise<any> {
        const formData = new FormData(this.form)
        return new ApiService().checkCaptcha(formData)
    }

    private attachChangeListener(): void {
        if (this.listCheckboxes) {
            this.listCheckboxes.forEach((checkbox) => {
                checkbox.addEventListener(EventType.CHANGE, () => {
                    if (this.closeTimeout) {
                        // cancel banner animation timeout
                        clearTimeout(this.closeTimeout)
                        // unset timeout until the next submit attempt
                        this.closeTimeout = undefined
                        // reset form immediately
                        this.resetForm(false)
                    }

                    const card = checkbox.closest<HTMLElement>(
                        `.${className.card}`
                    )

                    if (checkbox.checked) {
                        this.listIds.push(checkbox.value)
                        if (card) {
                            card.classList.add(className.cardChecked)
                        }
                    } else {
                        this.listIds = this.listIds.filter(
                            (listId) => listId !== checkbox.value
                        )
                        if (card) {
                            card.classList.remove(className.cardChecked)
                        }
                    }

                    this.updateUITotal()

                    if (this.hasSlidingDrawer) {
                        if (this.listIds.length > 0) {
                            window.dispatchEvent(
                                new CustomEvent(SlidingDrawerEvent.OPEN, {
                                    detail: this.drawerId
                                })
                            )
                        } else {
                            window.dispatchEvent(
                                new CustomEvent(SlidingDrawerEvent.CLOSE, {
                                    detail: this.drawerId
                                })
                            )
                        }
                    }
                })
            })
        }
    }

    private attachPaidNewsLetterListener(): void {
        // Set X-API-U-TOKEN header before send htmx request
        // if LR token is not available in local storage, let server side validation handle this
        document.body.addEventListener(
            'htmx:configRequest',
            (evt: CustomEvent) => {
                // get token from local storage
                const token = localStorage.getItem(LocalStorageKey.LRTOKENKEY)
                const lrTokenKey =
                    token && token !== 'null' && token !== 'undefined'
                        ? token
                        : undefined
                if (lrTokenKey) {
                    // set token in header
                    evt.detail.headers['X-API-U-TOKEN'] = lrTokenKey
                } else {
                    // Do nothing at client side, let server side validation handle this
                    logger('warn', 'User Access Token is not available')
                }
            }
        )
        document.addEventListener('htmx:afterRequest', (evt: CustomEvent) => {
            if (evt.detail.target.id === attrName.exclusiveCta) {
                let errorFlag = false
                // eslint-disable-next-line no-magic-numbers
                if (evt.detail.successful) {
                    //Check if user registered or unregistered
                    if (evt.detail.xhr.response === 'registered') {
                        evt.detail.target.outerHTML =
                            '<a class="button button--primary exclusive-registered-cta" href="/my-subscriptions/"' +
                            'name="newsletter-exclusive-cta">Manage subscriptions</a>'
                    } else if (evt.detail.xhr.response === 'unregistered') {
                        const url = evt.detail.target.dataset.url
                        evt.detail.target.outerHTML =
                            `<a class="button button--primary exclusive-cta" href="${url}" ` +
                            ' name="newsletter-exclusive-cta">Subscribe</a>'
                    } else {
                        errorFlag = true
                    }
                } else {
                    errorFlag = true
                }
                if (errorFlag) {
                    evt.detail.target.outerHTML =
                        '<div class="newsletter__error-cta" ><p>We\'re sorry, but something went wrong. Please try ' +
                        'again later, or <a href= "/contact/" title="contact-us">contact us</a> if the issue ' +
                        'persists.</p></div>'
                }
            }
        })
    }

    // Load and initialize Sailthru asynchronously
    private async loadSourceScript(): Promise<void> {
        await isDomReady()

        ScriptService.getInstance()
            .loadScript({
                name: 'newsletter_sailthru',
                src: Newsletter.scriptSource,
                loaded: false
            })
            .then(() => {
                // Initialize Sailthru
                this.sailthru = window.Sailthru
                void this.sailthru.init({ customerId: this.accountId })
            })
            .catch((error: Error) => console.error(error))
    }

    // Pull the format form values and call 3rd party API
    // https://getstarted.sailthru.com/developers/api-client/javascript/#userSignUp
    public async submitForm(): Promise<void> {
        let captcha = true
        if (this.formType === NewsletterFormType.MULTIPLE) {
            const result = await this.getRecaptchaResponse()
            if (result?.error) {
                captcha = false
                if (this.captchaError) {
                    this.captchaError.hidden = false
                }
            }
        }

        if (this.emailValue && captcha) {
            // lists format for sailthru API
            // [listId, listId] => { listId: 1, listId: 1 }
            const signUpLists: Record<string, number> = this.listIds.reduce(
                (obj, item) => ({ ...obj, [item]: 1 }),
                {}
            )

            // vars data format for sailthru API
            // Subscribe: {"Authour_Name": "Y"}, Unsubscribe: {"Author_Name": "N"}
            const subscribeToggle = this.isSubscribed ? 'N' : 'Y'

            const signUpVars = JSON.parse(
                `{"${this.authorName}": "${subscribeToggle}"}` || '{}'
            )

            if (this.emailServiceName === EmailServices.MAILCHIMP) {
                // Submit data to MailChimp
                this.postMailChimpApi()
                    .then((response) => {
                        this.handleSubmitSuccess()
                        logger('info', 'Mailchimp response: ' + response)
                    })
                    .catch((error) => {
                        if (
                            error.message.includes('permanently deleted') ||
                            error.message.includes('compliance state')
                        ) {
                            if (this.formType === NewsletterFormType.MULTIPLE) {
                                this.displayedErrorInnerHTML =
                                    'There’s an issue with one of your selected newsletters, please contact customer service at 902-700-0133 or <a href="mailto:saltwirecustserv@postmedia.com">saltwirecustserv@postmedia.com</a>'
                            } else {
                                this.displayedErrorInnerHTML =
                                    'To sign up for a newsletter again, please email Customer Service at <a href="mailto:saltwirecustserv@postmedia.com">saltwirecustserv@postmedia.com</a>'
                            }
                        } else if (error.message.includes('too many signups')) {
                            this.displayedErrorInnerHTML =
                                'Newsletter signup is temporarily blocked. Please try again later.'
                        } else {
                            this.displayedErrorInnerHTML =
                                this.originalErrorInnerHTML
                        }
                        this.handleSubmitError()
                        logger('error', 'Mailchimp error: ' + error)
                    })
            } else {
                // Submit data to Sailthru
                this.displayedErrorInnerHTML = this.originalErrorInnerHTML
                this.sailthru.integration('userSignUp', {
                    email: this.emailValue,
                    lists: signUpLists,
                    vars: signUpVars,
                    source: window.location,
                    onSuccess: (): void => this.handleSubmitSuccess(),
                    onError: (): void => this.handleSubmitError()
                })
                this.sailthru.integration('customEvent', {
                    name: 'setOptOutNone',
                    email: this.emailValue,
                    onSuccess: (): void =>
                        logger(
                            'info',
                            'Sailthru customEvent setOptOutNone  completed'
                        ),
                    onError: (): void =>
                        logger(
                            'error',
                            'Sailthru customEvent setOptOutNone failed'
                        )
                })
            }
        }
    }

    /**
     * Subscribe User email to given lists via `POST /newsletters/subscribe/` API which send requests to MailChimp API
     */
    public async postMailChimpApi(): Promise<Response> {
        const formData = new FormData()
        formData.append('email', this.emailValue)
        /*
        convert
        ["20cf2557fd|2e5c849c30","20cf2557fd|2e5c849c30b","1e93567a0a"]

        to (to remove duplicate and combine)
        {
            20cf2557fd: {
                    "2e5c849c30": true,
                     "2e5c849c30b": true
                },
                1e93567a0a: {
                }
        }

        and then convert to
        [
            {
                "list_id": "20cf2557fd",
                "interests": {
                    "2e5c849c30": true,
                     "2e5c849c30b": true
                }
            },
            {"list_id":"1e93567a0a"}
        ]
        */
        const mcListIds = this.listIds.reduce((acc, listId) => {
            const [list_id, interest_id] = listId.split('|')
            if (!acc[list_id]) {
                acc[list_id] = {}
            }
            if (interest_id) {
                acc[list_id][interest_id] = true
            }
            return acc
        }, {} as Record<string, Record<string, boolean>>)

        const mcListIdsArray = Object.entries(mcListIds).map(
            ([list_id, interests]) => {
                return {
                    list_id,
                    interests
                }
            }
        )

        formData.append('list_ids', JSON.stringify(mcListIdsArray))

        const csrf_token = this.el.querySelector(
            'input[name=csrfmiddlewaretoken]'
        )?.value

        const requestOptions = {
            method: 'POST',
            body: formData,
            headers: csrf_token
                ? {
                      // csrf token header
                      'X-CSRFToken': csrf_token
                  }
                : undefined
        }

        return await fetch('/newsletters/subscribe/', requestOptions).then(
            async (response) => {
                if (response.ok) {
                    return response.json()
                }
                throw new Error(
                    'Request failed with status ' +
                        response.status +
                        (await response.text())
                )
            }
        )
    }

    // Sailthru submit success callback
    public handleSubmitSuccess(): void {
        this.submitSuccess = true

        // Hide designated elements on success
        if (this.successReplaceAreas) {
            this.successReplaceAreas.forEach((area) => {
                area.hidden = true
            })
        }
        // remove error class from handleSubmitError
        if (this.emailField) {
            this.emailField.classList.remove(className.emailFieldError)
        }
        if (this.formErrorMsg) {
            this.formErrorMsg.hidden = true
        }

        // if form type has cards show the signed up success state on the card
        if (this.formType === NewsletterFormType.MULTIPLE && this.form) {
            const checkedCards = this.getElements(
                `.${className.card}.${className.cardChecked}`,
                this.form
            )

            checkedCards.forEach((card) => {
                card.classList.add(className.cardSuccess)

                const checkbox = this.getElement<HTMLInputElement>(
                    'input',
                    card
                )
                if (checkbox) {
                    checkbox.disabled = true
                }

                const successMsg = this.getElement(
                    `.${className.cardSuccessMessage}`,
                    card
                )
                if (successMsg) {
                    successMsg.hidden = false
                }
            })
        }

        // Show success screen
        if (this.formSuccessMsg) {
            this.formSuccessMsg.hidden = false
        }

        if (this.formType === NewsletterFormType.AUTHOR) {
            const subscribeMsg = this.getElement(
                `.${className.subscribeMessage}`,
                this.el
            )
            const unsubscribeMsg = this.getElement(
                `.${className.unsubscribeMessage}`,
                this.el
            )
            if (!subscribeMsg || !unsubscribeMsg) {
                return
            }

            if (this.isSubscribed) {
                this.isSubscribed = false
                this.unsubscribeBtn.hidden = true
                // udate message
                unsubscribeMsg.hidden = false
                subscribeMsg.hidden = true
                // close drawer
                this.successSwitchDrawer()
            } else {
                this.isSubscribed = true
                this.unsubscribeBtn.hidden = false
                // update message
                unsubscribeMsg.hidden = true
                subscribeMsg.hidden = false
                // switch drawer
                this.successCloseDrawer()
            }
        }

        if (this.formType === NewsletterFormType.MULTIPLE) {
            // Trigger Analytics event from `/newsletters` page when user submit form
            dispatchAnalyticsFormSubmissionEvent(
                'Newsletter',
                this.email as HTMLInputElement,
                false
            )
            // Reset recaptcha after successfull submit
            window.grecaptcha.reset()
            // Reset recaptcha error in UI
            if (this.captchaError) {
                this.captchaError.hidden = true
            }

            // after timeout reset drawer success state and
            // list for another round of sign up
            this.resetForm(true)
        }

        if (this.formType === NewsletterFormType.SINGLE && this.form) {
            dispatchAnalyticsFormSubmissionEvent(
                'Newsletter',
                this.form.elements[0] as HTMLInputElement,
                false
            )
        }
    }

    // Sailthru submit error callback
    public handleSubmitError(): void {
        // Show error screen
        if (this.emailField) {
            this.emailField.classList.add(className.emailFieldError)
        }
        if (this.formErrorMsg) {
            this.formErrorMsg.innerHTML = this.displayedErrorInnerHTML
            this.formErrorMsg.hidden = false
        }
    }

    // save email value, validate, perform UI feedback
    private validateEmail(): boolean {
        let validEmail = false
        // Collect email value
        if (this.email) {
            this.emailValue = this.email.value
        }

        // Test if email's format is valid
        if (this.emailValue && validateEmail(this.emailValue)) {
            validEmail = true
            // Hide email error
            if (this.emailField) {
                this.emailField.classList.remove(className.emailFieldError)
            }
            if (this.emailError) {
                this.emailError.hidden = true
            }

            if (this.email) {
                this.email.setAttribute(AriaAttr.INVALID, 'false')
            }
        } else {
            validEmail = false
            // Show email error
            if (this.emailField) {
                this.emailField.classList.add(className.emailFieldError)
            }
            if (this.emailError) {
                this.emailError.hidden = false
            }
            if (this.email) {
                this.email.setAttribute(AriaAttr.INVALID, 'true')
            }
        }
        return validEmail
    }

    private updateUITotal(): void {
        if (!this.totalDisplay) {
            return
        }
        const total = String(this.listIds.length)
        this.totalDisplay.textContent = total
    }

    private resetForm(animation: boolean): void {
        if (this.hasSlidingDrawer && animation) {
            window.addEventListener(SlidingDrawerEvent.CLOSED, () =>
                this.resetFormUI()
            )
            this.successCloseDrawer()
        } else {
            this.resetFormUI()
        }
    }

    private resetFormUI(): void {
        // reset list IDs & total UI
        if (this.submitSuccess) {
            this.listIds = []
            this.updateUITotal()
            this.submitSuccess = false
        }

        // hide success screen
        if (this.formSuccessMsg) {
            this.formSuccessMsg.hidden = true
        }

        // show original screens
        if (this.successReplaceAreas) {
            this.successReplaceAreas.forEach((area) => {
                area.hidden = false
            })
        }
    }

    private successCloseDrawer(): void {
        this.closeTimeout = setTimeout(() => {
            window.dispatchEvent(
                new CustomEvent(SlidingDrawerEvent.CLOSE, {
                    detail: this.drawerId
                })
            )
        }, this.drawerTimeout)

        window.removeEventListener(SlidingDrawerEvent.CLOSED, () =>
            this.resetFormUI()
        )
    }

    private successSwitchDrawer(): void {
        // open banner to display unsubcribed message
        window.dispatchEvent(
            new CustomEvent(SlidingDrawerEvent.OPEN, {
                detail: this.drawerId
            })
        )

        this.closeTimeout = setTimeout(() => {
            // reset the sign up form
            this.resetFormUI()
            // re-open if the sign up banner is closed
            window.dispatchEvent(
                new CustomEvent(SlidingDrawerEvent.OPEN, {
                    detail: this.drawerId
                })
            )
        }, this.drawerTimeout)
    }
    /**
     * Get the user's token from local storage
     * @param callbackWithoutToken Callback function to execute if the token is not available
     * @param callbackWithToken Callback function to execute if the token is available
     * @returns {void}
     */
    public getLRToken(
        callbackWithoutToken: () => void,
        callbackWithToken: (token: string) => void
    ): void {
        attachEventHandlerWithFemFlagCheck('pnIdReady', (evt: CustomEvent) => {
            const token = localStorage.getItem(LocalStorageKey.LRTOKENKEY)
            const userToken =
                token && token !== 'null' && token !== 'undefined'
                    ? token
                    : undefined
            if (userToken) {
                logger('info', 'pnIdReady event handler executed', evt?.detail)
                callbackWithToken(userToken)
            } else {
                callbackWithoutToken()
            }
        })
    }
}
